/**
 * 📥 LABEL DOWNLOAD SERVICE
 * Service pour télécharger les étiquettes avec gestion des URLs expirées
 */

import { Injectable } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Observable, from, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export interface FreshLabelUrlResponse {
  success: boolean;
  downloadUrl?: string;
  expiresAt?: string;
  error?: string;
}

/**
 * #1857 lot 7 — refus EXPLICITE du serveur (ex. « Étiquette non trouvée pour … ») : son message
 * est transmis tel quel. Seule une panne de transport reçoit le message générique « Veuillez
 * réessayer » — qui ne sert à rien quand le serveur a répondu qu'il n'y a pas d'étiquette.
 */
export class LabelUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LabelUnavailableError';
  }
}

@Injectable({
  providedIn: 'root'
})
export class LabelDownloadService {

  constructor(private functions: Functions) {}

  /**
   * Obtenir une URL fraîche pour télécharger une étiquette
   * Résout le problème des URLs expirées pour les anciens colis
   */
  getFreshLabelUrl(trackingNumber: string): Observable<string> {
    console.log('📥 [LABEL SERVICE] Demande URL fraîche pour:', trackingNumber);

    return from(this.callGetFreshLabelUrl(trackingNumber)).pipe(
      map(result => {
        if (!result.data.success || !result.data.downloadUrl) {
          throw new LabelUnavailableError(result.data.error || 'Impossible de générer l\'URL de téléchargement');
        }

        console.log('✅ [LABEL SERVICE] URL fraîche obtenue (expire:', result.data.expiresAt, ')');
        return result.data.downloadUrl;
      }),
      catchError(error => {
        console.error('❌ [LABEL SERVICE] Erreur:', error);
        if (error instanceof LabelUnavailableError) {
          return throwError(() => error);
        }
        return throwError(() => new Error(
          'Erreur lors de la génération du lien de téléchargement. Veuillez réessayer.'
        ));
      })
    );
  }

  /** Appel de la callable, isolé pour que les tests pilotent la réponse du serveur. */
  protected callGetFreshLabelUrl(trackingNumber: string): Promise<{ data: FreshLabelUrlResponse }> {
    const getFreshUrl = httpsCallable<{ trackingNumber: string }, FreshLabelUrlResponse>(
      this.functions,
      'getFreshLabelUrl'
    );
    return getFreshUrl({ trackingNumber });
  }

  /**
   * Télécharger directement l'étiquette
   * Ouvre une nouvelle fenêtre avec l'URL signée fraîche
   */
  downloadLabel(trackingNumber: string, filename?: string): Observable<void> {
    return this.getFreshLabelUrl(trackingNumber).pipe(
      map(url => {
        // Ouvrir dans une nouvelle fenêtre
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.download = filename || `etiquette-${trackingNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        console.log('✅ [LABEL SERVICE] Téléchargement lancé');
      }),
      catchError(error => {
        console.error('❌ [LABEL SERVICE] Erreur téléchargement:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Vérifier si une URL est probablement expirée
   * (basé sur la présence du paramètre Expires dans l'URL)
   */
  isUrlExpired(url: string): boolean {
    if (!url) return true;

    try {
      const urlObj = new URL(url);
      const expiresParam = urlObj.searchParams.get('Expires');

      if (!expiresParam) return false; // Pas d'expiration détectable

      const expiresTimestamp = parseInt(expiresParam, 10);
      const now = Math.floor(Date.now() / 1000);

      return now >= expiresTimestamp;
    } catch {
      return true; // En cas d'erreur, considérer comme expiré
    }
  }

  /**
   * Obtenir une URL de téléchargement (régénère si expirée)
   */
  getDownloadUrl(trackingNumber: string, existingUrl?: string): Observable<string> {
    // Si URL existante et non expirée, la réutiliser
    if (existingUrl && !this.isUrlExpired(existingUrl)) {
      console.log('✅ [LABEL SERVICE] URL existante encore valide');
      return from(Promise.resolve(existingUrl));
    }

    // Sinon, régénérer une URL fraîche
    console.log('🔄 [LABEL SERVICE] URL expirée ou manquante, régénération...');
    return this.getFreshLabelUrl(trackingNumber);
  }
}
